import { type Tag } from '@/lib/types';
import { IconButton, TextField } from '@mui/material';
import { useContext, useState } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import Dialog from '@/components/Dialog';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import useApi from '@/lib/api/useApi';
import TagsContext from '../TagsContext';
import Space from '@/components/Space';
import { FormattedMessage } from 'react-intl';
import FormattedHtmlMessage from '@/components/FormattedHtmlMessage';

type TagInfoEditorProps = {
  tag: Tag;
};

const TagInfoEditor = ({ tag }: TagInfoEditorProps) => {
  const api = useApi();
  const [, setTags] = useContext(TagsContext);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [info, setInfo, infoHasChanged, reenableInfoSync] = useComplexSwrState(
    tag.info,
  );
  const [saving, setSaving] = useState(false);

  const cancelChanges = (): void => {
    setDialogOpen(false);

    if (infoHasChanged) {
      setInfo(tag.info);
      reenableInfoSync();
    }
  };

  const saveChanges = async (): Promise<void> => {
    setDialogOpen(false);
    setSaving(true);

    const updatedInfo =
      info === null || info.trim().length === 0 ? null : info.trim();

    const updatedTag = await api.put<Tag>(`editor/tags/${tag.id}/info`, {
      info: updatedInfo,
    });

    setTags(tags => {
      tags[tag.id] = {
        ...tags[tag.id],
        ...updatedTag,
      };
    });

    setSaving(false);
    reenableInfoSync();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={() => setDialogOpen(true)}
        disabled={saving}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Dialog.Title>
          <FormattedMessage id="editor.model_editor.field_editor.tags_editor.dialog.tags_manager.tag.info_editor.dialog.title" />
        </Dialog.Title>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedHtmlMessage
              id="editor.model_editor.field_editor.tags_editor.dialog.tags_manager.tag.info_editor.dialog.text"
              values={{
                tagName: tag.name,
              }}
            />
          </Dialog.ContentText>
          <Space height={12} />
          <TextField
            variant="filled"
            label={
              <FormattedMessage id="editor.model_editor.field_editor.tags_editor.dialog.tags_manager.tag.info_editor.dialog.input_label" />
            }
            value={info ?? ''}
            onChange={e => setInfo(e.target.value)}
            minRows={4}
            maxRows={6}
            multiline
            autoFocus
            fullWidth
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.CancelButton onClick={cancelChanges} />
          <Dialog.Actions.SaveButton
            onClick={saveChanges}
            disabled={!infoHasChanged}
          />
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

export default TagInfoEditor;
